import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { heroSlides } from "@/mocks/heroSlides";
import { movies } from "@/mocks/movies";

export default function HeroCarousel() {
  const [index, setIndex] = useState(0);
  const total = heroSlides.length;

  const go = useCallback(
    (next: number) => setIndex((next + total) % total),
    [total]
  );

  useEffect(() => {
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % total);
    }, 5500);
    return () => clearInterval(timer);
  }, [total]);

  const slide = heroSlides[index];
  const movie = movies.find((m) => m.id === slide.movieId);

  return (
    <section className="relative h-[420px] w-full overflow-hidden md:h-[600px]">
      {heroSlides.map((s, i) => (
        <div
          key={s.id}
          className={`absolute inset-0 transition-opacity duration-700 ${
            i === index ? "opacity-100" : "opacity-0"
          }`}
        >
          <img
            src={s.image}
            alt={movie?.title ? `${movie.title} 剧照` : "电影天堂焦点推荐"}
            title="电影天堂焦点推荐"
            className="h-full w-full object-cover object-top"
          />
        </div>
      ))}
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/35 to-black/20"></div>

      <div className="relative z-10 mx-auto flex h-full w-full flex-col justify-end px-4 pb-16 md:px-6 md:pb-20">
        {movie && (
          <div key={slide.id} className="mp-fade-up max-w-2xl">
            <span className="mb-3 inline-flex items-center gap-1.5 rounded-full bg-accent-500 px-3 py-1 text-xs font-bold text-primary-950">
              <i className="ri-fire-fill"></i>
              焦点推荐
            </span>
            <h1 className="font-heading text-3xl font-black text-white md:text-5xl">
              {movie.title}
            </h1>
            <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-white/90">
              <span className="flex items-center gap-1 text-accent-400">
                <i className="ri-star-fill"></i>
                {movie.rating.toFixed(1)}
              </span>
              <span>{movie.year}</span>
              <span>{movie.region}</span>
              <span>{movie.types.join(" / ")}</span>
            </div>
            <p className="mt-3 max-w-xl text-sm leading-relaxed text-white/80 md:text-base">
              {slide.tagline}
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to={`/detail/${movie.id}`}
                className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-600"
              >
                <i className="ri-play-fill"></i>
                查看详情
              </Link>
              <Link
                to="/category/all"
                className="inline-flex items-center gap-2 rounded-md border border-white/50 bg-white/10 px-5 py-2.5 text-sm font-medium text-white backdrop-blur transition-colors hover:bg-white/20"
              >
                <i className="ri-apps-2-line"></i>
                浏览全部
              </Link>
            </div>
          </div>
        )}
      </div>

      <div className="absolute bottom-5 right-4 z-10 flex items-center gap-2 md:right-6">
        <button
          type="button"
          aria-label="上一张"
          onClick={() => go(index - 1)}
          className="flex h-9 w-9 items-center justify-center rounded-full border border-white/40 bg-black/30 text-white transition-colors hover:bg-black/50"
        >
          <i className="ri-arrow-left-s-line text-lg"></i>
        </button>
        <button
          type="button"
          aria-label="下一张"
          onClick={() => go(index + 1)}
          className="flex h-9 w-9 items-center justify-center rounded-full border border-white/40 bg-black/30 text-white transition-colors hover:bg-black/50"
        >
          <i className="ri-arrow-right-s-line text-lg"></i>
        </button>
      </div>

      <div className="absolute bottom-6 left-1/2 z-10 flex -translate-x-1/2 items-center gap-2">
        {heroSlides.map((s, i) => (
          <button
            key={s.id}
            type="button"
            aria-label={`切换到第 ${i + 1} 张`}
            onClick={() => setIndex(i)}
            className={`h-2 rounded-full transition-all ${
              i === index ? "w-6 bg-accent-500" : "w-2 bg-white/60"
            }`}
          />
        ))}
      </div>
    </section>
  );
}